import type { OnlineStats, onlineRanges } from '@nexo/types';
import { SegmentedControl } from '@nexo/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatNumber, formatTime } from '../../lib/format';

/**
 * Live «online now» counter: signed-in people (web / desktop) and visitors without an account,
 * refreshed every few seconds while the panel is visible, plus the last hour per minute.
 */
export function OnlineNow() {
  const { t } = useTranslation();
  const [minutes, setMinutes] = useState<(typeof onlineRanges)[number]>(60);
  const online = useQuery({
    queryKey: ['admin', 'online', minutes],
    queryFn: () => api.admin.online(minutes),
    refetchInterval: 5_000,
    placeholderData: keepPreviousData,
  });
  const data = online.data;

  return (
    <section className="mb-6 rounded-xl border border-border p-4">
      <div className="flex items-center gap-2">
        <span className="relative flex size-2">
          <span className="absolute inset-0 animate-ping rounded-full bg-success/60" />
          <span className="relative size-2 rounded-full bg-success" />
        </span>
        <h2 className="text-sm font-semibold">{t('En línea ahora')}</h2>
        <SegmentedControl
          label={t('Periodo')}
          size="sm"
          className="ml-auto"
          value={String(minutes)}
          onChange={(value) => setMinutes(Number(value) as (typeof onlineRanges)[number])}
          options={[
            { value: '60', label: t('1 h') },
            { value: '360', label: t('6 h') },
            { value: '1440', label: t('24 h') },
          ]}
        />
        {data && (
          <span className="text-xs text-subtle max-sm:hidden">
            {t('Actualizado a las {{time}}', { time: formatTime(data.at) })}
          </span>
        )}
      </div>

      {data ? (
        <>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <Counter
              label={t('Con cuenta')}
              value={data.users}
              tone="text-accent"
              detail={t('{{web}} web · {{desktop}} escritorio', {
                web: formatNumber(data.web),
                desktop: formatNumber(data.desktop),
              })}
            />
            <Counter
              label={t('Sin cuenta')}
              value={data.visitors}
              tone="text-glow"
              detail={t('Visitantes de la web e invitados')}
            />
            <Counter
              label={t('Total')}
              value={data.users + data.visitors}
              detail={t('Personas con Nexo abierto')}
            />
          </div>
          <OnlineChart data={data} />
        </>
      ) : (
        <p className="mt-3 text-sm text-muted">
          {online.error ? t('No se pudo cargar') : t('Cargando…')}
        </p>
      )}
    </section>
  );
}

function Counter({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: number;
  detail: string;
  tone?: string;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted">{label}</p>
      {/* Keyed by value: the number pops when it changes. */}
      <p
        key={value}
        className={`animate-pop mt-1 text-3xl font-semibold tracking-tight tabular-nums ${tone ?? ''}`}
      >
        {formatNumber(value)}
      </p>
      <p className="mt-1 text-xs text-subtle">{detail}</p>
    </div>
  );
}

const CHART_HEIGHT = 180;
const PAD = { top: 12, right: 12, bottom: 24, left: 32 };

/**
 * People online over the chosen range: one line with an account, one without. Points sit at their
 * real time (missing minutes, e.g. during a deploy, show as gaps), and hovering shows the values.
 */
function OnlineChart({ data }: { data: OnlineStats }) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const node = box.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(240, entry.contentRect.width));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const history = data.history;
  const end = new Date(data.at).getTime();
  const start = end - data.rangeMinutes * 60_000;
  const bucketMs = data.bucketMinutes * 60_000;
  const peak = Math.max(0, ...history.map((h) => Math.max(h.users, h.visitors)));
  // Whole-number scale with a little headroom: 0…max in 2–4 steps.
  const step = Math.max(1, Math.ceil(peak / 3));
  const max = Math.max(step * 3, 3);
  const innerW = width - PAD.left - PAD.right;
  const innerH = CHART_HEIGHT - PAD.top - PAD.bottom;
  const x = (at: string) => PAD.left + ((new Date(at).getTime() - start) / (end - start)) * innerW;
  const y = (value: number) => PAD.top + innerH - (value / max) * innerH;

  /** Polyline segments, broken where samples are missing. */
  const segments = (pick: (h: OnlineStats['history'][number]) => number) => {
    const out: string[][] = [];
    let previous: number | null = null;
    for (const h of history) {
      const time = new Date(h.at).getTime();
      if (previous === null || time - previous > bucketMs * 2.5) out.push([]);
      out.at(-1)?.push(`${x(h.at).toFixed(1)},${y(pick(h)).toFixed(1)}`);
      previous = time;
    }
    return out;
  };
  const ticks = [0, step, step * 2, step * 3].filter((v) => v <= max);
  const timeTicks = Array.from({ length: 5 }, (_, i) => start + ((end - start) * i) / 4);
  const point = hover !== null ? history[hover] : undefined;

  function onMove(event: MouseEvent<SVGSVGElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    let best: number | null = null;
    let bestDistance = Infinity;
    history.forEach((h, i) => {
      const distance = Math.abs(x(h.at) - px);
      if (distance < bestDistance) {
        best = i;
        bestDistance = distance;
      }
    });
    setHover(best);
  }

  return (
    <div ref={box} className="relative mt-4">
      {history.length === 0 ? (
        <p className="py-10 text-center text-xs text-subtle">
          {t('La gráfica se irá llenando minuto a minuto.')}
        </p>
      ) : (
        <svg
          width={width}
          height={CHART_HEIGHT}
          role="img"
          aria-label={t('Personas en línea en el periodo elegido')}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
          className="block"
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke="var(--color-border)"
                strokeDasharray={tick === 0 ? undefined : '3 4'}
              />
              <text
                x={PAD.left - 8}
                y={y(tick)}
                dy="0.32em"
                textAnchor="end"
                className="fill-subtle text-[10px] tabular-nums"
              >
                {tick}
              </text>
            </g>
          ))}
          {timeTicks.map((time, i) => (
            <text
              key={time}
              x={PAD.left + (innerW * i) / 4}
              y={CHART_HEIGHT - 6}
              textAnchor={i === 0 ? 'start' : i === 4 ? 'end' : 'middle'}
              className="fill-subtle text-[10px] tabular-nums"
            >
              {i === 4 ? t('ahora') : formatTime(new Date(time).toISOString())}
            </text>
          ))}
          {segments((h) => h.users).map((points) => (
            <polygon
              key={`area-${points[0]}`}
              points={`${points.join(' ')} ${points.at(-1)?.split(',')[0]},${y(0)} ${points[0]?.split(',')[0]},${y(0)}`}
              fill="var(--color-accent)"
              opacity={0.12}
            />
          ))}
          {segments((h) => h.visitors).map((points) => (
            <polyline
              key={`v-${points[0]}`}
              points={points.join(' ')}
              fill="none"
              stroke="var(--color-glow)"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          ))}
          {segments((h) => h.users).map((points) => (
            <polyline
              key={`u-${points[0]}`}
              points={points.join(' ')}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          ))}
          {point && (
            <g>
              <line
                x1={x(point.at)}
                x2={x(point.at)}
                y1={PAD.top}
                y2={PAD.top + innerH}
                stroke="var(--color-border-strong)"
              />
              <circle cx={x(point.at)} cy={y(point.users)} r={3.5} fill="var(--color-accent)" />
              <circle cx={x(point.at)} cy={y(point.visitors)} r={3.5} fill="var(--color-glow)" />
            </g>
          )}
        </svg>
      )}
      {point && (
        <div
          className="pointer-events-none absolute top-0 z-10 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs shadow-float"
          style={{
            left: Math.min(Math.max(x(point.at) - 70, 0), width - 140),
          }}
        >
          <p className="font-medium tabular-nums">
            {formatTime(point.at)}
            {data.bucketMinutes > 1 && ` · ${t('máximo en {{n}} min', { n: data.bucketMinutes })}`}
          </p>
          <p className="text-accent">
            {t('Con cuenta')}: {formatNumber(point.users)}
          </p>
          <p className="text-glow">
            {t('Sin cuenta')}: {formatNumber(point.visitors)}
          </p>
        </div>
      )}
      <div className="mt-1 flex items-center gap-4 pl-8 text-[11px] text-subtle">
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 rounded bg-accent" /> {t('Con cuenta')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 rounded bg-glow" /> {t('Sin cuenta')}
        </span>
        <span className="ml-auto">{t('Máximo del periodo: {{n}}', { n: formatNumber(peak) })}</span>
      </div>
    </div>
  );
}
